import { useEffect, useState } from 'react';
import { Modal, Button, Field, inputClass } from '@/components/ui';
import { ComboSelect } from './ComboSelect';
import { TAILOR_ACTION_TYPES } from '@/lib/constants';
import { useApi } from '@/hooks/useApi';
import api from '@/lib/api';
import type { ServiceProvider, TailorAction } from '@/lib/types';

// Add or edit a tailor action. "Take to the tailor" (add mode only) makes it a
// pending to-do; unchecked records a completed alteration. A tailor (service
// provider) can be selected or created inline; it's stamped onto the item's
// service record when the action is taken in, so the out-for-service label reads
// "At <tailor>".
export function AddTailorActionModal({
  isOpen,
  onClose,
  itemId,
  onDone,
  action = null,
}: {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
  onDone: () => void;
  action?: TailorAction | null;
}) {
  const isEdit = !!action;
  const { data } = useApi<{ serviceProviders: ServiceProvider[] }>(isOpen ? '/service-providers' : null);
  const providers = data?.serviceProviders ?? [];

  const [actionType, setActionType] = useState('');
  const [notes, setNotes] = useState('');
  const [actionDate, setActionDate] = useState('');
  const [providerId, setProviderId] = useState('');
  const [providerName, setProviderName] = useState('');
  const [takeToTailor, setTakeToTailor] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill (edit) or reset (add) each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (action) {
      setActionType(action.actionType);
      setNotes(action.notes ?? '');
      setActionDate(action.actionDate ?? '');
      setProviderId(action.providerId ?? '');
      setProviderName(action.providerId ? '' : action.providerName ?? '');
    } else {
      setActionType('');
      setNotes('');
      setActionDate('');
      setProviderId('');
      setProviderName('');
      setTakeToTailor(true);
    }
  }, [isOpen, action]);

  // Resolve the chosen tailor to { providerId, providerName }, creating a new
  // service provider (type 'tailor') if a new name was typed.
  const resolveProvider = async (): Promise<{ providerId: string | null; providerName: string | null }> => {
    if (providerId) {
      return { providerId, providerName: providers.find((p) => p.id === providerId)?.name ?? null };
    }
    const typed = providerName.trim();
    if (!typed) return { providerId: null, providerName: null };
    const existing = providers.find((p) => p.name.toLowerCase() === typed.toLowerCase());
    if (existing) return { providerId: existing.id, providerName: existing.name };
    try {
      const res = await api.post<{ serviceProvider: { id: string; name: string } }>('/service-providers', {
        name: typed,
        providerType: 'tailor',
      });
      if (res?.serviceProvider?.id) {
        return { providerId: res.serviceProvider.id, providerName: res.serviceProvider.name };
      }
    } catch {
      /* best-effort; fall through to a name-only snapshot */
    }
    return { providerId: null, providerName: typed };
  };

  const save = async () => {
    if (!actionType.trim()) {
      setError('Choose or type an action.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const provider = await resolveProvider();
      if (isEdit && action) {
        await api.patch(`/tailor-actions/${action.id}`, {
          actionType: actionType.trim(),
          notes: notes.trim() || null,
          actionDate: actionDate || null,
          providerId: provider.providerId,
          providerName: provider.providerName,
        });
      } else {
        await api.post(`/items/${itemId}/tailor-actions`, {
          actionType: actionType.trim(),
          notes: notes.trim() || undefined,
          actionDate: actionDate || undefined,
          pending: takeToTailor,
          providerId: provider.providerId ?? undefined,
          providerName: provider.providerName ?? undefined,
        });
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the tailor action.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Tailor Action' : 'Add Tailor Action'}
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button fullWidth onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Field label="Action">
          <ComboSelect
            value={actionType}
            options={[...TAILOR_ACTION_TYPES]}
            onChange={setActionType}
            placeholder="e.g. Shorten Sleeves"
          />
        </Field>
        <Field label="Tailor">
          <select
            className={inputClass}
            value={providerId}
            onChange={(e) => {
              setProviderId(e.target.value);
              if (e.target.value) setProviderName('');
            }}
          >
            <option value="">Select a tailor…</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        {!providerId && (
          <Field label="Or add a new tailor">
            <input
              className={inputClass}
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              placeholder="e.g. Jeeves of Belgravia"
            />
          </Field>
        )}
        <Field label={takeToTailor && !isEdit ? 'Target date (optional)' : 'Date'}>
          <input
            type="date"
            value={actionDate}
            onChange={(e) => setActionDate(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Notes">
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inputClass}
            placeholder="Any details for the tailor…"
          />
        </Field>
        {!isEdit && (
          <>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={takeToTailor}
                onChange={(e) => setTakeToTailor(e.target.checked)}
                className="w-4 h-4 rounded border-border"
              />
              <span className="text-sm text-foreground">Take to the tailor</span>
            </label>
            <p className="text-xs text-muted-foreground">
              {takeToTailor
                ? 'Adds this to your Pending Tailor Actions until you take the item in.'
                : 'Records a completed alteration in this item’s history.'}
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
